import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../api/supabase';
import { useAuth } from '../../context/AuthContext';
import TypeSearch from '../../components/common/TypeSearch';
import SupplierPicker from '../../components/common/SupplierPicker';
import { postJournal } from '../../api/journal';
import { ACCOUNTS, payableAccountForSupplierType, cogsAccountForSupplierType } from '../../api/accounts';
import { usePasscode } from '../../components/common/PasscodeModal';
import { isPackageUnlocked, markPackageUnlocked } from './PackageDetail';

let _keyCounter = 1;
const newRow = () => ({ _key: ++_keyCounter, supplierId: '', supplierLabel: '', supplierType: '', notes: '', costAmount: '', currencyId: '', currencyCode: '', exchangeRate: '1' });

export default function PackageForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEdit = !!id;
  const username = user?.email?.split('@')[0] || 'unknown';

  const [form, setForm] = useState({
    package_name: '', country: '', destination: '', package_type: '',
    num_days: '', description: '', departure_date: '', return_date: '',
    min_pax: '', max_pax: '', is_active: false,
  });
  const [fitForm, setFitForm] = useState({
    client_id: '', client_label: '', num_pax: '1',
    sell_per_pax: '', sell_currency_id: '',
    care_of_agent_id: '', care_of_agent_label: '',
  });
  const [fitSuppliers, setFitSuppliers] = useState([newRow()]);
  const [passengers, setPassengers] = useState([{ name: '', ticket_number: '' }]);

  const [currencies, setCurrencies] = useState([]);
  const [agents, setAgents] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const isFIT = form.package_type === 'FIT';

  const { prompt: promptPasscode, PasscodeModal } = usePasscode();
  const [gateReady, setGateReady] = useState(() => !isEdit || isPackageUnlocked(id));

  // Editing a package unlocks everything (metadata here AND the cost/rate/selling-price/
  // commission editing on PackageDetail.js) as one gated action — not two separate ungated ones.
  useEffect(() => {
    if (!isEdit || isPackageUnlocked(id)) return;
    (async () => {
      try {
        await promptPasscode();
        markPackageUnlocked(id);
        setGateReady(true);
      } catch {
        navigate(`/packages/${id}`);
      }
    })();
  }, [id, isEdit]);

  useEffect(() => {
    if (isEdit) supabase.from('packages').select('*').eq('id', id).single().then(({ data }) => { if (data) setForm(data); });
  }, [id, isEdit]);

  useEffect(() => {
    if (!isFIT || isEdit) return;
    supabase.from('currencies').select('id, code').eq('is_active', true).order('code').then(({ data }) => {
      setCurrencies(data || []);
      const lkr = (data || []).find(c => c.code === 'LKR');
      if (lkr) {
        setFF('sell_currency_id', lkr.id);
        setFitSuppliers(rows => rows.map(r => ({ ...r, currencyId: lkr.id, currencyCode: 'LKR' })));
      }
    });
    supabase.from('agents').select('id, tls_code, full_name').eq('is_active', true).order('tls_code').then(({ data }) => {
      setAgents((data || []).map(a => ({ ...a, label: `${a.tls_code} — ${a.full_name}` })));
    });
  }, [isFIT, isEdit]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setFF = (k, v) => setFitForm(f => ({ ...f, [k]: v }));

  const updateSupplierRow = (key, fields) =>
    setFitSuppliers(rows => rows.map(r => r._key === key ? { ...r, ...fields } : r));
  const removeSupplierRow = (key) =>
    setFitSuppliers(rows => rows.filter(r => r._key !== key));
  const addSupplierRow = () =>
    setFitSuppliers(rows => [...rows, newRow()]);

  // All cost inputs are per pax; totals = per pax × numPax
  const numPax = parseInt(fitForm.num_pax) || 1;
  const costPerPaxLKR = fitSuppliers.reduce((sum, r) =>
    sum + (parseFloat(r.costAmount) || 0) * (parseFloat(r.exchangeRate) || 1), 0);
  const totalCostLKR = costPerPaxLKR * numPax;
  const sellPerPax = parseFloat(fitForm.sell_per_pax) || 0;
  const totalSell = sellPerPax * numPax;
  const gp = totalSell - totalCostLKR;
  const gpPct = totalSell > 0 ? ((gp / totalSell) * 100).toFixed(1) : 0;

  const handleSubmit = async () => {
    if (!form.package_name) return setError('Package name is required');
    // Pax drives the Budgeted side of the package P&L (Est. Revenue/Cost scale off it) —
    // left at the silent default of 1, that comparison is meaningless for any real group size.
    if (!isFIT && (!form.min_pax || parseInt(form.min_pax) < 1)) return setError('Number of pax is required — this is what the Budgeted P&L is calculated against.');
    if (isFIT && !isEdit && !fitForm.client_id) return setError('Client is required for FIT packages');
    if (form.return_date && form.departure_date && form.return_date <= form.departure_date) return setError('Return date must be after departure date');
    setSaving(true); setError('');
    const clean = {
      ...form,
      num_days: form.num_days !== '' ? parseInt(form.num_days) : null,
      max_pax: form.max_pax !== '' ? parseInt(form.max_pax) : null,
      min_pax: form.min_pax !== '' ? parseInt(form.min_pax) : 1,
      departure_date: form.departure_date || null,
      return_date: form.return_date || null,
    };
    try {
      if (isEdit) {
        await supabase.from('packages').update({ ...clean, updated_by: username, updated_at: new Date().toISOString() }).eq('id', id);
        navigate(`/packages/${id}`);
        return;
      }

      const yy = String(new Date().getFullYear()).slice(-2);
      const typePrefix = clean.package_type === 'FIT' ? 'FIT' : 'GR';
      const countryCode = (clean.country || 'XXX').toUpperCase().slice(0, 3);
      const refPrefix = `${typePrefix}-${yy}-${countryCode}`;
      const { data: lastPkg } = await supabase.from('packages').select('package_code').ilike('package_code', `${refPrefix}-%`).order('package_code', { ascending: false }).limit(1);
      const lastSeq = lastPkg?.[0]?.package_code ? parseInt(lastPkg[0].package_code.split('-').pop()) || 0 : 0;
      const package_code = `${refPrefix}-${String(lastSeq + 1).padStart(3, '0')}`;
      const { data: pkg, error: pkgErr } = await supabase.from('packages').insert({ ...clean, package_code, created_by: username }).select().single();
      if (pkgErr) throw pkgErr;

      if (!isFIT) {
        navigate(`/packages/${pkg.id}`);
        return;
      }

      // FIT: auto-create booking
      const gpPctNum = totalSell > 0 ? parseFloat(((gp / totalSell) * 100).toFixed(2)) : 0;
      const booking_ref = 'BK' + Date.now().toString().slice(-7);

      const { data: bk, error: bkErr } = await supabase.from('bookings').insert({
        booking_ref,
        client_id: fitForm.client_id,
        package_id: pkg.id,
        care_of_agent_id: fitForm.care_of_agent_id || null,
        num_pax: numPax,
        booking_status: 'PENDING',
        payment_status: 'UNPAID',
        total_cost: totalCostLKR || null,
        total_sell: totalSell || null,
        sell_currency_id: fitForm.sell_currency_id || null,
        gross_profit: gp || null,
        gp_margin_percent: gpPctNum || null,
        exchange_rate: 1,
        travel_date: form.departure_date || null,
        return_date: form.return_date || null,
        notes: form.description || null,
        passengers: passengers.filter(p => p.name || p.ticket_number).length > 0 ? passengers.filter(p => p.name || p.ticket_number) : null,
        created_by: username,
      }).select().single();
      if (bkErr) throw bkErr;

      // Insert booking_suppliers rows
      const supplierRows = fitSuppliers
        .filter(r => r.supplierId && parseFloat(r.costAmount) > 0)
        .map(r => ({
          booking_id: bk.id,
          supplier_id: r.supplierId,
          cost_amount: parseFloat(r.costAmount) * numPax,
          currency_id: r.currencyId || null,
          exchange_rate: parseFloat(r.exchangeRate) || 1,
          notes: r.notes || null,
          amount_paid: 0,
          payment_status: 'UNPAID',
        }));
      if (supplierRows.length) {
        await supabase.from('booking_suppliers').insert(supplierRows);

        // Post commitment journal: DR COGS / CR AP per supplier
        for (const r of fitSuppliers.filter(r => r.supplierId && parseFloat(r.costAmount) > 0)) {
          const exchRate = parseFloat(r.exchangeRate) || 1;
          // Amount stays in the supplier's own currency — postJournal() applies the
          // fx conversion once. Do not pre-multiply by exchRate here, or the fx gets
          // applied twice and the AP balance is inflated by another factor of exchRate.
          const totalForeign = parseFloat(r.costAmount) * numPax;
          const desc = `${pkg.package_code} — ${r.notes || r.supplierLabel}`;
          await postJournal({
            date: new Date().toISOString().slice(0, 10),
            description: `Supplier commitment — ${pkg.package_code} — ${r.supplierLabel}`,
            reference_type: 'package',
            reference_id: pkg.id,
            currency_id: r.currencyId || null,
            exchange_rate: exchRate,
            lines: [
              { account_id: cogsAccountForSupplierType(r.supplierType, r.currencyCode), debit_amount: totalForeign, credit_amount: 0, description: desc },
              { account_id: payableAccountForSupplierType(r.supplierType), debit_amount: 0, credit_amount: totalForeign, description: desc, party_type: 'supplier', party_id: r.supplierId },
            ],
            created_by: username,
          });
        }
      }

      // Auto-create proforma as DRAFT
      const prefix = `PRO-${yy}`;
      const { count: invCount } = await supabase.from('invoices').select('*', { count: 'exact', head: true }).ilike('invoice_number', `${prefix}-%`);
      const invoiceNum = `${prefix}-${String((invCount || 0) + 1).padStart(4, '0')}`;
      const lkrId = currencies.find(c => c.code === 'LKR')?.id;
      const amountPerPax = numPax > 0 ? totalSell / numPax : totalSell;
      const lineItems = [{ description: form.package_name || 'Travel Package', sub: null, qty: numPax, unitPrice: amountPerPax, amount: totalSell }];
      // cost_amount is per-pax, already converted to LKR — exchange_rate must stay 1 here
      // since the FX conversion already happened (costAmount × exchangeRate below). Setting
      // per_pax:true lets the GP display (cost_amount × totalPax) reconstruct the true total.
      const costItems = fitSuppliers
        .filter(r => r.supplierId && parseFloat(r.costAmount) > 0)
        .map(r => ({
          supplier_id: r.supplierId,
          supplier_label: r.supplierLabel || '',
          cost_amount: parseFloat(r.costAmount) * (parseFloat(r.exchangeRate) || 1),
          currency_id: lkrId,
          exchange_rate: 1,
          per_pax: true,
        }));
      await supabase.from('invoices').insert({
        invoice_number: invoiceNum,
        invoice_type: 'PROFORMA',
        client_id: fitForm.client_id,
        source_type: 'booking',
        source_id: bk.id,
        issued_date: new Date().toISOString().slice(0, 10),
        status: 'DRAFT',
        line_items: lineItems,
        cost_items: costItems,
        created_by: username,
      });

      navigate(`/bookings/${bk.id}`);
    } catch (err) { setError(err.message || 'Error saving'); setSaving(false); }
  };

  const gpColor = gp >= 0 ? 'var(--success)' : 'var(--danger)';

  if (isEdit && !gateReady) {
    return <div>{PasscodeModal}</div>;
  }

  return (
    <div>
      {PasscodeModal}
      <div className="page-header">
        <div>
          <div className="page-title">{isEdit ? 'Edit Package' : 'New Package'}</div>
          <div className="page-subtitle">{isFIT && !isEdit ? 'FIT — booking and proforma will be created automatically' : 'After creating, add supplier rates and selling prices'}</div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-secondary" onClick={() => navigate(-1)}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>{saving ? 'Saving...' : isEdit ? 'Save Changes' : isFIT ? 'Create FIT & Proforma' : 'Create Package'}</button>
        </div>
      </div>
      {error && <div style={{ color: 'var(--danger)', marginBottom: 16, padding: '10px 14px', background: 'rgba(239,68,68,0.06)', borderRadius: 7 }}>{error}</div>}
      <div className="card">
        <div className="form-grid">
          <div className="form-group full-width">
            <label className="form-label">Package Name *</label>
            <input className="form-input" value={form.package_name} onChange={e => set('package_name', e.target.value)} placeholder="e.g. Dubai 5 Nights 6 Days — Smith Family" />
          </div>
          <div className="form-group">
            <label className="form-label">Country</label>
            <input className="form-input" value={form.country || ''} onChange={e => set('country', e.target.value)} placeholder="e.g. UAE, Thailand, Singapore" />
          </div>
          <div className="form-group">
            <label className="form-label">Destination / City</label>
            <input className="form-input" value={form.destination || ''} onChange={e => set('destination', e.target.value)} placeholder="e.g. Dubai, Phuket" />
          </div>
          <div className="form-group">
            <label className="form-label">Number of Days</label>
            <input className="form-input" type="number" min="1" value={form.num_days || ''} onChange={e => set('num_days', e.target.value)} placeholder="e.g. 6" />
          </div>
          <div className="form-group">
            <label className="form-label">Package Type</label>
            <select className="form-input" value={form.package_type || ''} onChange={e => set('package_type', e.target.value)}>
              <option value="">-- Select --</option>
              {['Group','FIT'].map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Departure Date</label>
            <input className="form-input" type="date" value={form.departure_date || ''} onChange={e => set('departure_date', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Return Date</label>
            <input className="form-input" type="date" value={form.return_date || ''} onChange={e => set('return_date', e.target.value)} />
          </div>
          {!isFIT && (
            <div className="form-group">
              <label className="form-label">Pax *</label>
              <input className="form-input" type="number" min="1" value={form.min_pax} onChange={e => set('min_pax', e.target.value)} placeholder="Number of pax this package is built for" />
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>Used to calculate the Budgeted side of this package's P&L</div>
            </div>
          )}
          <div className="form-group full-width">
            <label className="form-label">Description</label>
            <textarea className="form-textarea" rows={4} value={form.description || ''} onChange={e => set('description', e.target.value)} placeholder="Itinerary, notes..." />
          </div>
          {isEdit && (
            <div className="form-group">
              <label className="form-label">Status</label>
              <select className="form-input" value={form.is_active ? 'true' : 'false'} onChange={e => set('is_active', e.target.value === 'true')}>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
          )}
        </div>
      </div>

      {isFIT && !isEdit && (
        <div className="card" style={{ marginTop: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 16 }}>FIT Booking Details</div>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Client *</label>
              <TypeSearch
                fetchFn={async (q) => {
                  let qb = supabase.from('clients').select('id, full_name, client_code').eq('is_active', true).limit(40);
                  if (q) qb = qb.or(`full_name.ilike.%${q}%,client_code.ilike.%${q}%`);
                  else qb = qb.order('full_name');
                  const { data } = await qb;
                  return (data || []).map(c => ({ ...c, label: `${c.full_name} (${c.client_code})` }));
                }}
                value={fitForm.client_label || ''}
                displayKey="label" placeholder="Type client name…"
                onSelect={c => { setFF('client_id', c.id); setFF('client_label', c.label); }}
                onClear={() => { setFF('client_id', ''); setFF('client_label', ''); }}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Care of Agent</label>
              <TypeSearch
                options={agents} value={fitForm.care_of_agent_label} displayKey="label"
                placeholder="TLS code or agent name…"
                onSelect={a => { setFF('care_of_agent_id', a.id); setFF('care_of_agent_label', a.label); }}
                onClear={() => { setFF('care_of_agent_id', ''); setFF('care_of_agent_label', ''); }}
              />
            </div>
            <div className="form-group">
              <label className="form-label">No. of Pax</label>
              <input className="form-input" type="text" inputMode="numeric" value={fitForm.num_pax} onChange={e => setFF('num_pax', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Sell per Pax</label>
              <input className="form-input" type="text" inputMode="decimal" value={fitForm.sell_per_pax} onChange={e => setFF('sell_per_pax', e.target.value)} placeholder="0.00" />
            </div>
            <div className="form-group">
              <label className="form-label">Sell Currency</label>
              <TypeSearch options={currencies} value={currencies.find(c => c.id === fitForm.sell_currency_id)?.code || ''} displayKey="code" placeholder="Currency…" onSelect={c => setFF('sell_currency_id', c.id)} onClear={() => setFF('sell_currency_id', '')} />
            </div>
          </div>

          {/* Supplier cost rows */}
          <div style={{ marginTop: 20 }}>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10 }}>Supplier Costs</div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--border)', background: 'var(--bg)' }}>
                    <th style={{ padding: '7px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, color: 'var(--text-muted)', minWidth: 180 }}>Supplier</th>
                    <th style={{ padding: '7px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, color: 'var(--text-muted)', minWidth: 140 }}>Description</th>
                    <th style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 600, fontSize: 11, color: 'var(--text-muted)', minWidth: 110 }}>Cost / Pax</th>
                    <th style={{ padding: '7px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, color: 'var(--text-muted)', minWidth: 90 }}>Currency</th>
                    <th style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 600, fontSize: 11, color: 'var(--text-muted)', minWidth: 80 }}>FX Rate</th>
                    <th style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 600, fontSize: 11, color: 'var(--text-muted)', minWidth: 110 }}>LKR / Pax</th>
                    <th style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 600, fontSize: 11, color: 'var(--text-muted)', minWidth: 110 }}>Total LKR</th>
                    <th style={{ width: 32 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {fitSuppliers.map(row => {
                    const lkrPerPax = (parseFloat(row.costAmount) || 0) * (parseFloat(row.exchangeRate) || 1);
                    const totalLKR = lkrPerPax * numPax;
                    return (
                      <tr key={row._key} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '5px 6px' }}>
                          <SupplierPicker row={{ supplier_id: row.supplierId, supplier_label: row.supplierLabel, sub_supplier_id: row.subSupplierId }}
                            onChange={async patch => {
                              const upd = { supplierLabel: patch.supplier_label ?? row.supplierLabel, subSupplierId: patch.sub_supplier_id ?? null };
                              if ('supplier_id' in patch) {
                                upd.supplierId = patch.supplier_id;
                                const { data: sup } = patch.supplier_id ? await supabase.from('suppliers').select('supplier_type').eq('id', patch.supplier_id).maybeSingle() : { data: null };
                                upd.supplierType = sup?.supplier_type || '';
                              }
                              updateSupplierRow(row._key, upd);
                            }} />
                        </td>
                        <td style={{ padding: '5px 6px' }}>
                          <input className="form-input" style={{ fontSize: 13, padding: '6px 8px' }}
                            value={row.notes} placeholder="e.g. Hotel 3N, Flights"
                            onChange={e => updateSupplierRow(row._key, { notes: e.target.value })} />
                        </td>
                        <td style={{ padding: '5px 6px' }}>
                          <input className="form-input" type="text" inputMode="decimal" style={{ fontSize: 13, padding: '6px 8px', textAlign: 'right' }}
                            value={row.costAmount} placeholder="0.00"
                            onChange={e => updateSupplierRow(row._key, { costAmount: e.target.value })} />
                        </td>
                        <td style={{ padding: '5px 6px' }}>
                          <TypeSearch
                            options={currencies}
                            value={row.currencyCode}
                            displayKey="code"
                            placeholder="CCY"
                            onSelect={c => updateSupplierRow(row._key, { currencyId: c.id, currencyCode: c.code })}
                            onClear={() => updateSupplierRow(row._key, { currencyId: '', currencyCode: '' })}
                          />
                        </td>
                        <td style={{ padding: '5px 6px' }}>
                          <input className="form-input" type="text" inputMode="decimal" style={{ fontSize: 13, padding: '6px 8px', textAlign: 'right' }}
                            value={row.exchangeRate} placeholder="1"
                            onChange={e => updateSupplierRow(row._key, { exchangeRate: e.target.value })} />
                        </td>
                        <td style={{ padding: '5px 10px', textAlign: 'right', color: lkrPerPax > 0 ? '#1d4ed8' : 'var(--text-muted)' }}>
                          {lkrPerPax > 0 ? lkrPerPax.toLocaleString('en-LK', { maximumFractionDigits: 0 }) : '—'}
                        </td>
                        <td style={{ padding: '5px 10px', textAlign: 'right', fontWeight: 600, color: totalLKR > 0 ? '#1d4ed8' : 'var(--text-muted)' }}>
                          {totalLKR > 0 ? totalLKR.toLocaleString('en-LK', { maximumFractionDigits: 0 }) : '—'}
                        </td>
                        <td style={{ padding: '5px 4px' }}>
                          {fitSuppliers.length > 1 && (
                            <button type="button" style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '0 4px' }}
                              onClick={() => removeSupplierRow(row._key)}>×</button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: '2px solid var(--border)', background: 'var(--bg)' }}>
                    <td colSpan={5} style={{ padding: '8px 10px', fontWeight: 700, fontSize: 12 }}>Total Cost (LKR)</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, color: '#1d4ed8' }}>
                      {costPerPaxLKR > 0 ? costPerPaxLKR.toLocaleString('en-LK', { maximumFractionDigits: 0 }) : '—'}
                    </td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, color: '#1d4ed8' }}>
                      {totalCostLKR > 0 ? totalCostLKR.toLocaleString('en-LK', { maximumFractionDigits: 0 }) : '—'}
                    </td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
            <button type="button" className="btn btn-secondary" style={{ fontSize: 12, padding: '5px 14px', marginTop: 8 }}
              onClick={addSupplierRow}>+ Add Supplier</button>
          </div>

          {/* Margin summary */}
          {(fitForm.sell_per_pax || totalCostLKR > 0) && (
            <div style={{ marginTop: 20 }}>
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>Margin Summary</div>
              <div style={{ borderRadius: 7, border: '1px solid var(--border)', overflow: 'hidden', fontSize: 13 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: 'var(--bg)', borderBottom: '1px solid var(--border)' }}>
                      <th style={{ padding: '7px 12px', textAlign: 'left', fontWeight: 600, fontSize: 11, color: 'var(--text-muted)' }}></th>
                      <th style={{ padding: '7px 12px', textAlign: 'right', fontWeight: 600, fontSize: 11, color: 'var(--text-muted)' }}>Per Pax (LKR)</th>
                      <th style={{ padding: '7px 12px', textAlign: 'right', fontWeight: 600, fontSize: 11, color: 'var(--text-muted)' }}>Total ({numPax} pax)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '7px 12px' }}>Sell</td>
                      <td style={{ padding: '7px 12px', textAlign: 'right' }}>{sellPerPax.toLocaleString()}</td>
                      <td style={{ padding: '7px 12px', textAlign: 'right', fontWeight: 600 }}>{totalSell.toLocaleString()}</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '7px 12px' }}>Cost <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>({fitSuppliers.filter(r => r.supplierId).length} supplier{fitSuppliers.filter(r => r.supplierId).length !== 1 ? 's' : ''})</span></td>
                      <td style={{ padding: '7px 12px', textAlign: 'right', color: '#1d4ed8' }}>{costPerPaxLKR.toLocaleString('en-LK', { maximumFractionDigits: 0 })}</td>
                      <td style={{ padding: '7px 12px', textAlign: 'right', fontWeight: 600, color: '#1d4ed8' }}>{totalCostLKR.toLocaleString('en-LK', { maximumFractionDigits: 0 })}</td>
                    </tr>
                    <tr style={{ background: 'var(--bg)' }}>
                      <td style={{ padding: '7px 12px', fontWeight: 700 }}>GP</td>
                      <td style={{ padding: '7px 12px', textAlign: 'right', fontWeight: 700, color: gpColor }}>{(gp / numPax).toLocaleString('en-LK', { maximumFractionDigits: 0 })}</td>
                      <td style={{ padding: '7px 12px', textAlign: 'right', fontWeight: 700, color: gpColor }}>{gp.toLocaleString('en-LK', { maximumFractionDigits: 0 })} <span style={{ fontWeight: 400, fontSize: 12 }}>({gpPct}%)</span></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Passenger list */}
          <div style={{ marginTop: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>Passengers</div>
              <button type="button" className="btn btn-secondary" style={{ fontSize: 12, padding: '4px 12px' }}
                onClick={() => setPassengers(p => [...p, { name: '', ticket_number: '' }])}>
                + Add Row
              </button>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border)' }}>
                  <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', width: 36 }}>#</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)' }}>Passenger Name</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)' }}>Ticket Number</th>
                  <th style={{ width: 36 }}></th>
                </tr>
              </thead>
              <tbody>
                {passengers.map((p, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '4px 8px', color: 'var(--text-muted)', fontSize: 12 }}>{i + 1}</td>
                    <td style={{ padding: '4px 8px' }}>
                      <input className="form-input" style={{ fontSize: 13, padding: '5px 8px' }}
                        value={p.name} placeholder="Full name"
                        onChange={e => setPassengers(ps => ps.map((r, j) => j === i ? { ...r, name: e.target.value } : r))} />
                    </td>
                    <td style={{ padding: '4px 8px' }}>
                      <input className="form-input" style={{ fontSize: 13, padding: '5px 8px', fontFamily: 'monospace' }}
                        value={p.ticket_number} placeholder="e.g. 1234567890"
                        onChange={e => setPassengers(ps => ps.map((r, j) => j === i ? { ...r, ticket_number: e.target.value } : r))} />
                    </td>
                    <td style={{ padding: '4px 8px' }}>
                      {passengers.length > 1 && (
                        <button type="button" style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: 16, padding: '0 4px' }}
                          onClick={() => setPassengers(ps => ps.filter((_, j) => j !== i))}>×</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
